import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import apiClient from '../../services/apiClient'
import { Container } from '@/components/ui'
import { formatDbDateVi } from '@/utils/dbDateDisplay'

interface EnrolledCourse {
  id: number
  name: string
  thumbnail: string
  progress: number
  method?: string
  classroomName?: string | null
  startDate?: string | null
  expireDate?: string | null
  isGift?: boolean
  needsClassroomSelection?: boolean
  giftSelectDeadline?: string | null
  giftSelectExpired?: boolean
}

interface GiftClassroomOption {
  id: number
  name: string
  code: string
  startDate: string | null
  endDate: string | null
  schedule: string | null
  address: string | null
  isPast: boolean
}

const MyCoursesPage = () => {
  const { user } = useAuthStore()
  const [courses, setCourses] = useState<EnrolledCourse[]>([])
  const [loading, setLoading] = useState(true)
  const [selectCourse, setSelectCourse] = useState<EnrolledCourse | null>(null)
  const [classOptions, setClassOptions] = useState<GiftClassroomOption[]>([])
  const [classLoading, setClassLoading] = useState(false)
  const [classError, setClassError] = useState<string | null>(null)
  const [expired, setExpired] = useState(false)
  const [deadline, setDeadline] = useState<string | null>(null)
  const [selectingId, setSelectingId] = useState<number | null>(null)

  const fetchCourses = useCallback(async () => {
    try {
      const response = await apiClient.get('/student/my-courses')
      const data = response.data
      if (data.success) {
        setCourses(data.data)
      } else {
        console.error('Failed to fetch courses:', data.message)
      }
    } catch (error) {
      console.error('Error fetching courses:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchCourses()
  }, [fetchCourses])

  const formatDate = (dateString: string | null | undefined) => formatDbDateVi(dateString)

  const openSelectClass = async (course: EnrolledCourse, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setSelectCourse(course)
    setClassOptions([])
    setClassError(null)
    setExpired(false)
    setDeadline(course.giftSelectDeadline || null)
    setClassLoading(true)
    try {
      const res = await apiClient.get(`/student/courses/${course.id}/available-classrooms`)
      const data = res.data
      if (!data.success) {
        setClassError(data.message || 'Không tải được danh sách lớp')
        return
      }
      setExpired(!!data.data?.expired)
      setDeadline(data.data?.deadline || null)
      setClassOptions(Array.isArray(data.data?.items) ? data.data.items : [])
    } catch (err: any) {
      setClassError(err?.response?.data?.message || 'Không tải được danh sách lớp')
    } finally {
      setClassLoading(false)
    }
  }

  const confirmSelectClass = async (classroomId: number) => {
    if (!selectCourse) return
    setSelectingId(classroomId)
    setClassError(null)
    try {
      const res = await apiClient.post(`/student/courses/${selectCourse.id}/select-classroom`, {
        classroomId,
      })
      if (!res.data?.success) {
        setClassError(res.data?.message || 'Chọn lớp thất bại')
        return
      }
      setSelectCourse(null)
      setLoading(true)
      await fetchCourses()
    } catch (err: any) {
      setClassError(err?.response?.data?.message || 'Chọn lớp thất bại')
    } finally {
      setSelectingId(null)
    }
  }

  return (
    <div className="bg-eh-cream eh-section--sm min-h-full">
      <Container>
        <header className="mb-8 border-b border-eh-border pb-6">
          <p className="eh-eyebrow">Học viên</p>
          <h1 className="eh-title">Khóa học của tôi</h1>
          <p className="mt-3 text-eh-muted">
            Chào mừng {user?.fullName}, đây là các khóa học bạn đang tham gia.
          </p>
        </header>

        {loading ? (
          <p className="text-eh-muted">Đang tải dữ liệu...</p>
        ) : courses.length === 0 ? (
          <div className="border border-dashed border-eh-border px-6 py-12 text-center text-eh-muted">
            Bạn chưa có khóa học nào.{' '}
            <Link to="/courses" className="font-medium text-eh-forest-ink underline">
              Xem danh sách khóa học
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-0 border-t border-eh-border sm:grid-cols-2 lg:grid-cols-3">
            {courses.map((course) => (
              <Link
                key={course.id}
                to={`/student/courses/${course.id}`}
                className="group flex flex-col border-b border-eh-border bg-eh-cream transition-colors hover:bg-eh-keylime/40 sm:border-r lg:[&:nth-child(3n)]:border-r-0"
              >
                <div className="flex h-56 items-center justify-center border-b border-eh-border bg-white p-4 sm:h-64">
                  <img
                    src={course.thumbnail}
                    alt={course.name}
                    className="h-auto max-h-full w-auto max-w-full object-contain"
                  />
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <div className="flex flex-wrap items-start gap-2">
                    <h3 className="eh-heading text-[1.125rem] group-hover:underline">{course.name}</h3>
                    {course.isGift ? (
                      <span className="rounded-eh-pill bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
                        Tặng kèm
                      </span>
                    ) : null}
                  </div>

                  {course.method !== 'VIDEO' && (
                    <div className="mt-4 border border-eh-border bg-eh-keylime/60 px-3 py-3 text-sm text-eh-forest-ink">
                      <p className="font-medium">
                        Lớp: {course.classroomName || 'Chưa xếp lớp'}
                      </p>
                      <p className="mt-1 text-eh-muted">
                        Khai giảng: {formatDate(course.startDate)}
                      </p>
                      {course.needsClassroomSelection ? (
                        <button
                          type="button"
                          onClick={(e) => openSelectClass(course, e)}
                          className="eh-btn mt-3 !min-h-[40px] w-full !px-3 !py-2 text-sm"
                        >
                          Chọn lớp học
                        </button>
                      ) : null}
                      {course.giftSelectExpired ? (
                        <p className="mt-2 text-xs font-medium text-m2-error">
                          Đã hết hạn 6 tháng để tự chọn lớp. Liên hệ hỗ trợ nếu cần xếp lớp.
                        </p>
                      ) : null}
                    </div>
                  )}

                  {course.method === 'VIDEO' && (
                    <div className="mt-4 border border-eh-border bg-eh-mint/50 px-3 py-3 text-sm text-eh-forest-ink">
                      <p className="font-medium">Tự học qua video</p>
                      <p className="mt-1 text-eh-muted">
                        Thời hạn:{' '}
                        {course.expireDate ? formatDate(course.expireDate) : 'Không giới hạn'}
                      </p>
                      {course.needsClassroomSelection ? (
                        <button
                          type="button"
                          onClick={(e) => openSelectClass(course, e)}
                          className="eh-btn mt-3 !min-h-[40px] w-full !px-3 !py-2 text-sm"
                        >
                          Chọn lớp học
                        </button>
                      ) : null}
                    </div>
                  )}

                  <div className="mt-auto pt-5">
                    <div className="mb-1 flex justify-between text-eh-caption">
                      <span>Tiến độ hoàn thành</span>
                      <span>{course.progress}%</span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-eh-mint">
                      <div
                        className="h-2 rounded-full bg-eh-forest-ink"
                        style={{ width: `${course.progress}%` }}
                      />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </Container>

      {selectCourse ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="gift-select-class-title"
          onClick={() => setSelectCourse(null)}
        >
          <div
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-eh border border-eh-border bg-eh-cream p-5 shadow-eh-soft"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="gift-select-class-title" className="text-lg font-bold text-eh-forest-ink">
              Chọn lớp — {selectCourse.name}
            </h2>
            <p className="mt-2 text-sm text-eh-muted">
              Bạn có thể chọn lớp đã khai giảng (quá khứ) hoặc lớp sắp tới. Tài liệu lớp cũ dùng bản mới
              nhất của khóa. Hạn chọn:{' '}
              <strong>{deadline ? formatDate(deadline) : 'trong 6 tháng'}</strong>.
            </p>

            {classLoading ? (
              <p className="mt-4 text-sm text-eh-muted">Đang tải lớp...</p>
            ) : expired ? (
              <p className="mt-4 text-sm font-medium text-m2-error">
                Đã hết hạn 6 tháng để tự chọn lớp.
              </p>
            ) : classOptions.length === 0 ? (
              <p className="mt-4 text-sm text-eh-muted">
                Không có lớp phù hợp trong cửa sổ 6 tháng quanh ngày được tặng.
              </p>
            ) : (
              <ul className="mt-4 space-y-3">
                {classOptions.map((cl) => (
                  <li
                    key={cl.id}
                    className="rounded-eh border border-eh-border bg-white p-3 text-sm"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <p className="font-semibold text-eh-forest-ink">
                          {cl.name || cl.code}
                          {cl.isPast ? (
                            <span className="ml-2 text-xs font-medium text-eh-muted">
                              (Đã khai giảng)
                            </span>
                          ) : (
                            <span className="ml-2 text-xs font-medium text-eh-muted">(Sắp tới)</span>
                          )}
                        </p>
                        <p className="mt-1 text-eh-muted">
                          KG: {formatDate(cl.startDate)}
                          {cl.endDate ? ` — ${formatDate(cl.endDate)}` : ''}
                        </p>
                        {cl.schedule ? <p className="mt-0.5 text-eh-muted">{cl.schedule}</p> : null}
                      </div>
                      <button
                        type="button"
                        disabled={selectingId === cl.id}
                        onClick={() => confirmSelectClass(cl.id)}
                        className="eh-btn !min-h-[40px] shrink-0 !px-3 !py-2 text-sm disabled:opacity-50"
                      >
                        {selectingId === cl.id ? 'Đang lưu...' : 'Chọn'}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {classError ? <p className="mt-3 text-sm text-m2-error">{classError}</p> : null}

            <button
              type="button"
              className="eh-btn-secondary mt-5 w-full"
              onClick={() => setSelectCourse(null)}
            >
              Đóng
            </button>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default MyCoursesPage
